import React from "react";
import { useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "../../../../aws/auth";
import toast from "react-hot-toast";
import { AUTH_ROUTE } from "app/constants/route";

type Props = {};

const initialValues = {
  formValue: {
    username: "",
    confirmationCode: "",
    newPassword: "",
    confirmPassword: "",
  },
};

const useForgotPasswordForm = (props: Props) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [formValue, setFormValue] = React.useState(initialValues?.formValue);
  const [step, setStep] = React.useState<"request" | "confirm">("request");

  const updateFormValue = (key: string, value: any) => {
    try {
      const newFormValue = { ...formValue, [key]: value };
      setFormValue(newFormValue);
    } catch (err) {
      console.error("Error updating form value:", err);
    }
  };

  const requestResetCode = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);

      const result = await resetPassword(formValue.username);

      if (result.success) {
        toast.success("Reset code sent to your email!");
        setStep("confirm");
      }
    } catch (e) {
      console.error("Request reset code error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPasswordReset = async () => {
    if (isLoading) return;

    // Validate passwords match
    if (formValue.newPassword !== formValue.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    // Validate password strength
    if (formValue.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long!");
      return;
    }

    try {
      setIsLoading(true);

      const result = await confirmResetPassword({
        username: formValue.username,
        confirmationCode: formValue.confirmationCode,
        newPassword: formValue.newPassword,
      });

      if (result.success) {
        // Navigate to sign in after successful reset
        setTimeout(() => {
          navigate(AUTH_ROUTE);
        }, 2000);
      }
    } catch (e) {
      console.error("Confirm password reset error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitRequestCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await requestResetCode();
  };

  const onSubmitConfirmReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await confirmPasswordReset();
  };

  const resendCode = async () => {
    await requestResetCode();
  };

  return {
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    formValue,
    updateFormValue,
    isLoading,
    step,
    onSubmitRequestCode,
    onSubmitConfirmReset,
    resendCode,
  };
};

export default useForgotPasswordForm;
